import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Package, Plus, X, Upload, Image, File } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import monetizationLogo from "@/assets/monetization-logo-new.png";

interface AssetPack {
  id: string;
  name: string;
  description: string;
  files: UploadedFile[];
}

interface UploadedFile {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string;
}

export default function MonetizationAssetUpload() {
  const { projectId, productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState<any>(null);
  const [project, setProject] = useState<any>(null);
  const [assetPacks, setAssetPacks] = useState<AssetPack[]>([
    { id: crypto.randomUUID(), name: "", description: "", files: [] }
  ]);

  useEffect(() => {
    if (projectId && productId) {
      fetchData();
    }
  }, [projectId, productId]);

  const fetchData = async () => {
    try {
      const [projectRes, productRes] = await Promise.all([
        supabase.from("monetization_projects").select("*").eq("id", projectId).single(),
        supabase.from("monetization_products").select("*").eq("id", productId).single(),
      ]);

      if (projectRes.error) throw projectRes.error;
      if (productRes.error) throw productRes.error;

      setProject(projectRes.data);
      setProduct(productRes.data);

      // Load existing asset packs if any
      const existingContent = productRes.data.content as any;
      if (existingContent?.userAssetPacks && existingContent.userAssetPacks.length > 0) {
        setAssetPacks(existingContent.userAssetPacks);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load product");
    } finally {
      setLoading(false);
    }
  };

  const addAssetPack = () => {
    setAssetPacks([
      ...assetPacks,
      { id: crypto.randomUUID(), name: "", description: "", files: [] }
    ]);
  };

  const removeAssetPack = (packId: string) => {
    if (assetPacks.length === 1) {
      toast.error("You need at least one asset pack");
      return;
    }
    setAssetPacks(assetPacks.filter(p => p.id !== packId));
  };

  const updateAssetPack = (packId: string, field: "name" | "description", value: string) => {
    setAssetPacks(assetPacks.map(p => 
      p.id === packId ? { ...p, [field]: value } : p
    ));
  };

  const handleFileSelect = async (packId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newFiles: UploadedFile[] = Array.from(files).map(file => ({
      id: crypto.randomUUID(),
      name: file.name,
      type: file.type,
      size: file.size,
    }));

    setAssetPacks(assetPacks.map(p => 
      p.id === packId ? { ...p, files: [...p.files, ...newFiles] } : p
    ));

    toast.success(`Added ${newFiles.length} file(s) to this pack`);
  };

  const removeFile = (packId: string, fileId: string) => {
    setAssetPacks(assetPacks.map(p => 
      p.id === packId ? { ...p, files: p.files.filter(f => f.id !== fileId) } : p
    ));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (type: string) => {
    if (type.startsWith("image/")) return <Image className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const handleContinue = async () => {
    // Validate
    const invalidPacks = assetPacks.filter(p => !p.name.trim());
    if (invalidPacks.length > 0) {
      toast.error("Please give each asset pack a name");
      return;
    }

    const emptyPacks = assetPacks.filter(p => p.files.length === 0);
    if (emptyPacks.length > 0) {
      toast.error("Please add at least one file to each asset pack");
      return;
    }

    setSaving(true);
    try {
      // Save asset packs to product content
      const existingContent = product.content || {};
      await supabase
        .from("monetization_products")
        .update({
          content: {
            ...existingContent,
            userAssetPacks: assetPacks,
          },
          status: "assets_uploaded"
        })
        .eq("id", productId);

      // Navigate to build page
      navigate(`/app/monetization-ai/project/${projectId}/build/${productId}`);
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save asset packs");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="w-12 h-12 animate-spin text-green-500" />
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(`/app/monetization-ai/project/${projectId}/products`)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 rounded-xl overflow-hidden">
            <img src={monetizationLogo} alt="Monetization AI" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Upload Your Assets</h1>
            <p className="text-muted-foreground">Add the files for your asset pack product</p>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-8 ml-14">
          <div className="h-2 w-24 rounded-full bg-green-500" />
          <div className="h-2 w-24 rounded-full bg-green-500" />
          <div className="h-2 w-24 rounded-full bg-green-500" />
          <div className="h-2 w-24 rounded-full bg-green-500/50" />
          <div className="h-2 w-24 rounded-full bg-muted" />
        </div>

        {/* Product Info */}
        <div className="glass-card p-4 border-green-500/20 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Package className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="font-semibold">{product?.title}</p>
              <p className="text-sm text-muted-foreground">Define what's included in your asset pack</p>
            </div>
          </div>
        </div>

        {/* Asset Packs */}
        <div className="space-y-6 mb-8">
          {assetPacks.map((pack, index) => (
            <div key={pack.id} className="glass-card p-6 border-border/50">
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-display font-semibold text-lg">
                  Asset Pack {index + 1}
                </h3>
                {assetPacks.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeAssetPack(pack.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor={`name-${pack.id}`}>Pack Name *</Label>
                  <Input
                    id={`name-${pack.id}`}
                    placeholder="e.g., Instagram Story Templates, LUT Pack, Sound Effects"
                    value={pack.name}
                    onChange={(e) => updateAssetPack(pack.id, "name", e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor={`desc-${pack.id}`}>Description</Label>
                  <Textarea
                    id={`desc-${pack.id}`}
                    placeholder="What's included in this pack? What can buyers use it for?"
                    value={pack.description}
                    onChange={(e) => updateAssetPack(pack.id, "description", e.target.value)}
                    className="mt-1 min-h-[80px]"
                  />
                </div>

                {/* File Upload Area */}
                <div>
                  <Label>Files *</Label>
                  <div className="mt-1 border-2 border-dashed border-border/50 rounded-xl p-6 text-center hover:border-green-500/50 transition-colors">
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      id={`files-${pack.id}`}
                      onChange={(e) => handleFileSelect(pack.id, e)}
                    />
                    <label htmlFor={`files-${pack.id}`} className="cursor-pointer">
                      <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Click to upload or drag and drop
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Any file type supported
                      </p>
                    </label>
                  </div>

                  {/* File List */}
                  {pack.files.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {pack.files.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded bg-muted flex items-center justify-center text-muted-foreground">
                              {getFileIcon(file.type)}
                            </div>
                            <div>
                              <p className="text-sm font-medium truncate max-w-[200px]">
                                {file.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => removeFile(pack.id, file.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <p className="text-xs text-muted-foreground text-center pt-2">
                        {pack.files.length} file(s) added
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add Pack Button */}
          <Button
            variant="outline"
            onClick={addAssetPack}
            className="w-full border-dashed"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Asset Pack
          </Button>
        </div>

        {/* Continue Button */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={() => navigate(`/app/monetization-ai/project/${projectId}/products`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            variant="hero"
            size="lg"
            onClick={handleContinue}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : null}
            Continue to Build
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
